/**
 * A aba Arquivo: baixar, imprimir e compartilhar a peça em DOCX, PDF e HTML.
 * DOCX e PDF vêm do app (`onGerarArquivo`, no servidor); o HTML e a impressão
 * saem do próprio editor.
 *
 * Tudo parte do HTML de saída (`htmlDeSaida`), que já traz a formatação de cada
 * parágrafo em estilo inline: o Word e o navegador o leem sem o CSS do editor.
 * A impressão é a exceção: ela clona a FOLHA da tela (timbrado, cabeçalho,
 * rodapé, notas), para sair como se vê.
 */
import { PAPEIS, type DocumentoForense } from './documento';

/** Os formatos que a pessoa escolhe para baixar e para compartilhar. */
export type FormatoArquivo = 'docx' | 'pdf' | 'html';
/** O que o app gera (no servidor ou com uma lib). */
export type FormatoGerado = 'docx' | 'pdf';

/** "Contestação — 0807748-61/2022" → "Contestação - 0807748-61-2022": vale em qualquer sistema. */
export function nomeDeArquivo(nome: string | undefined, extensao: string): string {
  const base = (nome ?? '').replace(/[\\/:*?"<>|]+/g, '-').replace(/\p{Cc}+/gu, '-').replace(/\s+/g, ' ').replace(/[—–]/g, '-').trim().slice(0, 120) || 'Peça';
  return `${base}.${extensao}`;
}

export function baixarBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // O download começa no clique; o URL pode sair logo depois.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function escapar(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

function dimensoes(d: DocumentoForense): { largura: number; altura: number } {
  const p = PAPEIS[d.papel];
  return d.orientacao === 'paisagem' ? { largura: p.altura, altura: p.largura } : { largura: p.largura, altura: p.altura };
}

/** Papel e margens da peça em CSS de impressão. */
function regraDePagina(d: DocumentoForense, seletor = ''): string {
  const { largura, altura } = dimensoes(d);
  const m = d.margens;
  return `@page${seletor} { size: ${largura}cm ${altura}cm; margin: ${m.superior}cm ${m.direita}cm ${m.inferior}cm ${m.esquerda}cm; }`;
}

/** Página da web (.html) que abre sozinha, com o papel e as margens da peça. */
export function paginaHtml(html: string, d: DocumentoForense, titulo: string): Blob {
  const { largura } = dimensoes(d);
  const m = d.margens;
  const corpo = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${escapar(titulo)}</title>
<style>
${regraDePagina(d)}
body { margin: 0; background: #EDECEF; font-family: Arial, sans-serif; font-size: 12pt; color: #111; }
main { box-sizing: border-box; max-width: ${largura}cm; margin: 24px auto; padding: ${m.superior}cm ${m.direita}cm ${m.inferior}cm ${m.esquerda}cm; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.15); }
p { margin: 0; }
img { max-width: 100%; }
@media print { body { background: none; } main { max-width: none; margin: 0; padding: 0; box-shadow: none; } }
</style></head>
<body><main>${html}</main></body></html>`;
  return new Blob([corpo], { type: 'text/html;charset=utf-8' });
}

/** Texto puro: um parágrafo por linha, em branco entre eles; células separadas por tabulação. */
export function textoPuro(html: string): string {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]+>/g, '');
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  // O <br> (quebra de linha dentro do parágrafo) não tem texto: sem virar quebra ele
  // sumia — "SENHOR<br>JUIZ" virava "SENHORJUIZ". E o texto se lê por `textContent`:
  // o documento não é desenhado, e o `innerText` normaliza o espaço e engole a quebra.
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  const linhas: string[] = [];
  const visitar = (el: Element) => {
    for (const filho of Array.from(el.children)) {
      const tag = filho.tagName;
      if (tag === 'TABLE') {
        filho.querySelectorAll('tr').forEach((tr) => linhas.push(Array.from(tr.children).map((c) => (c.textContent ?? '').trim()).join('\t')));
        linhas.push('');
      } else if (tag === 'UL' || tag === 'OL') {
        Array.from(filho.children).forEach((li, i) => linhas.push(`${tag === 'OL' ? `${i + 1}.` : '•'} ${(li.textContent ?? '').trim()}`));
        linhas.push('');
      } else if (/^(P|H[1-6]|BLOCKQUOTE|PRE)$/.test(tag)) {
        linhas.push(filho.textContent ?? '');
        linhas.push('');
      } else if (filho.children.length) {
        visitar(filho);
      } else if ((filho.textContent ?? '').trim()) {
        linhas.push(filho.textContent!.trim());
        linhas.push('');
      }
    }
  };
  visitar(doc.body);
  return linhas.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

/**
 * Imprime a FOLHA como está na tela (timbrado, cabeçalho e rodapé, notas), num
 * iframe à parte: imprimir a janela levaria junto o app em volta do editor.
 * O "Salvar como PDF" do diálogo de impressão é o PDF da peça.
 */
export function imprimirFolha(folha: HTMLElement, d: DocumentoForense, titulo: string): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);
    const w = iframe.contentWindow!;
    const doc = w.document;
    const { largura, altura } = dimensoes(d);
    // Os estilos do app vêm junto: as variáveis de cor e o CSS do editor moram neles.
    const estilos = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map((n) => n.outerHTML).join('\n');
    const clone = folha.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[contenteditable]').forEach((n) => n.removeAttribute('contenteditable'));
    // A folha da tela tem as margens laterais no padding e o alto/baixo no cabeçalho;
    // as páginas que o navegador cria ao transbordar ganham as margens pelo @page.
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapar(titulo)}</title>${estilos}
<style>
@page { size: ${largura}cm ${altura}cm; margin: ${d.margens.superior}cm 0 ${d.margens.inferior}cm 0; }
@page :first { margin-top: 0; }
html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; overflow: visible !important; }
.cm-ef, .cm-ef-area, .cm-ef-coluna, .cm-ef-mesa { display: block !important; height: auto !important; max-height: none !important; overflow: visible !important; border: 0 !important; padding: 0 !important; margin: 0 !important; background: none !important; box-shadow: none !important; }
.cm-ef-folha { zoom: 1 !important; box-shadow: none !important; margin: 0 !important; width: auto !important; max-width: none !important; min-height: 0 !important; padding-bottom: 0 !important; }
.cm-ef-vao, .cm-ef-comparando, .ProseMirror-gapcursor, .ProseMirror-separator { display: none !important; }
.cm-ef-hf-livre:empty { display: none !important; }
.cm-ef-quebra-pagina { break-after: page; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; caret-color: transparent; }
</style></head>
<body><div class="cm-ef"><div class="cm-ef-area"><div class="cm-ef-coluna"><div class="cm-ef-mesa"></div></div></div></div></body></html>`);
    doc.close();
    doc.querySelector('.cm-ef-mesa')!.appendChild(doc.importNode(clone, true));
    let feito = false;
    const fim = () => {
      if (feito) return;
      feito = true;
      // Remover no mesmo tique cancelaria a impressão em alguns navegadores.
      window.setTimeout(() => { iframe.remove(); resolve(); }, 500);
    };
    const imprimir = () => {
      w.addEventListener('afterprint', fim, { once: true });
      w.focus();
      w.print();
      // Safari não dispara afterprint de dentro do iframe; o print() já bloqueou até fechar.
      window.setTimeout(fim, 1000);
    };
    // Fontes e imagens (timbrado) precisam estar prontas, senão saem em branco.
    const imagens = Array.from(doc.images).filter((i) => !i.complete).map((i) => new Promise((ok) => { i.onload = i.onerror = ok; }));
    const fontes = (doc as Document & { fonts?: FontFaceSet }).fonts?.ready ?? Promise.resolve();
    // No build, o CSS chega por <link>, que carrega assíncrono no iframe: sem esperá-lo,
    // a impressão podia sair sem o estilo da folha.
    const folhas = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
      .filter((l) => !l.sheet)
      .map((l) => new Promise((ok) => { l.onload = l.onerror = ok; }));
    void Promise.race([Promise.all([fontes, ...imagens, ...folhas]), new Promise((ok) => window.setTimeout(ok, 2500))]).then(imprimir);
  });
}

/** O navegador sabe compartilhar ARQUIVO (celular, Safari, Edge)? */
export function podeCompartilharArquivo(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function' || typeof File === 'undefined') return false;
  try {
    return navigator.canShare({ files: [new File(['x'], 'x.txt', { type: 'text/plain' })] });
  } catch {
    return false;
  }
}

/**
 * Compartilha pelo menu do sistema (WhatsApp, Mail, AirDrop…). `cancelado` = a
 * pessoa desistiu no menu (nada a dizer); `recusado` = o navegador não aceita este
 * arquivo (o Chrome recusa .docx no `canShare`) ou o gesto expirou enquanto o
 * servidor gerava o arquivo (Safari) — aí quem chama baixa e avisa.
 */
export async function compartilharArquivo(blob: Blob, nome: string, titulo: string): Promise<'ok' | 'cancelado' | 'recusado'> {
  const arquivo = new File([blob], nome, { type: blob.type });
  if (!navigator.canShare?.({ files: [arquivo] })) return 'recusado';
  try {
    await navigator.share({ files: [arquivo], title: titulo });
    return 'ok';
  } catch (err) {
    return err instanceof DOMException && err.name === 'AbortError' ? 'cancelado' : 'recusado';
  }
}

/**
 * E-mail com o texto no corpo. Os clientes de e-mail cortam `mailto:` longo
 * (~2 mil caracteres): acima disso o texto vai para a área de transferência e
 * o corpo diz para colar.
 */
export const LIMITE_MAILTO = 1800;
export function linkDeEmail(titulo: string, texto: string): { href: string; cabe: boolean } {
  // O limite é do LINK: espaço, quebra e acento viram %20, %0A, %C3%A7 (até 6 caracteres cada).
  const cabe = encodeURIComponent(texto).length <= LIMITE_MAILTO;
  const corpo = cabe ? texto : 'O texto da peça foi copiado: cole aqui (Ctrl+V).';
  return { href: `mailto:?subject=${encodeURIComponent(titulo)}&body=${encodeURIComponent(corpo)}`, cabe };
}

/** Contagens do painel Detalhes, como o "Contar palavras" do Word. */
export function detalhesDoTexto(texto: string): { palavras: number; caracteres: number; semEspacos: number; paragrafos: number } {
  const limpo = texto.trim();
  return {
    palavras: limpo ? limpo.split(/\s+/).length : 0,
    caracteres: limpo.replace(/\n/g, '').length,
    semEspacos: limpo.replace(/\s/g, '').length,
    paragrafos: limpo ? limpo.split(/\n\s*\n/).filter((p) => p.trim()).length : 0,
  };
}
